/**
 * Teams Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = TEAMS.
 * Single source of truth for Teams deployment configuration.
 */

import { z } from "zod";

import type { DeepPartial } from "../../../../shared/core/common.types";
import { BaseSettingsSchema } from "../Base.schema";
import { AllowedGroupSchema } from "./AllowedGroup.schema";
import { AllowedUserSchema } from "./AllowedUser.schema";
import { AssignmentStatusSchema } from "./AssignmentStatus.schema";
import { DistributionModeSchema } from "./DistributionMode.schema";

// ============================================================
// MAIN SCHEMA (Complete Teams Deployment Settings)
// ============================================================

/**
 * Complete Teams Deployment Settings Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for Deployment.deploymentSettings
 * when type = "TEAMS". Stored as JSON in Prisma, validated with this schema.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 */
export const TeamsDeploymentSettingsSchema = z
  .object({
    // Initial message sent when bot is added to a conversation
    initialMessage: z.string().optional(),

    // ============================================================
    // ACCESS CONTROL
    // ============================================================

    /**
     * Distribution mode - who can install this bot?
     * Follows Slack's pattern (deployment-level access control)
     */
    distributionMode: DistributionModeSchema.optional().default("everyone"),

    /**
     * Allowed users (specific users mode)
     * Stores user metadata for display (not just IDs like Slack!)
     */
    allowedUsers: z.array(AllowedUserSchema).optional(),

    /**
     * Allowed groups (specific groups mode)
     * Stores group metadata for display
     */
    allowedGroups: z.array(AllowedGroupSchema).optional(),

    /**
     * Assignment status (Graph API results)
     * Tracks success/failures when assigning app to users/groups
     */
    assignmentStatus: AssignmentStatusSchema.optional(),

    // ============================================================
    // LEGACY FIELDS (Backward Compatibility)
    // ============================================================

    // Azure AD / Microsoft Entra ID credentials (LEGACY - now in ApiKeyConnection)
    // These fields exist for backward compatibility with old deployments
    // New deployments store credentials in ApiKeyConnection
    tenantId: z.string().optional(),
    clientId: z.string().optional(), // Application (client) ID
    clientSecret: z.string().optional(),
    refreshToken: z.string().optional()

    // Publication metadata (teamsAppId, publishedAt) moved to ApiKeyConnection.settings
    // No longer stored in deploymentSettings
  })
  .merge(BaseSettingsSchema);

export type TeamsDeploymentSettings = z.infer<typeof TeamsDeploymentSettingsSchema>;

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default values for creating a new Teams deployment.
 * Used to initialize forms and ensure no missing fields.
 */
export const DEFAULT_TEAMS_SETTINGS: TeamsDeploymentSettings = {
  initialMessage: "",
  distributionMode: "everyone",
  allowedUsers: [],
  allowedGroups: []
};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete TEAMS deployment settings with defaults.
 *
 * This is the RECOMMENDED way to create Teams deployment settings.
 * Ensures all required fields are present with sensible defaults.
 *
 * NOTE: agentId is now Deployment column (FK to Agent).
 * This function returns only the settings JSON - agentId is passed separately
 * when creating/updating a Deployment.
 *
 * @param overrides - Partial settings to override defaults (optional)
 * @returns Complete, type-safe TeamsDeploymentSettings
 *
 * @example
 * // Create with defaults
 * const settings = createTeamsDeploymentSettings();
 *
 * @example
 * // Create with overrides
 * const settings = createTeamsDeploymentSettings({
 *   initialMessage: "Hello! I'm your Teams assistant."
 * });
 */
export function createTeamsDeploymentSettings(
  overrides?: DeepPartial<TeamsDeploymentSettings>
): TeamsDeploymentSettings {
  if (!overrides) return { ...DEFAULT_TEAMS_SETTINGS };

  return {
    ...DEFAULT_TEAMS_SETTINGS,
    ...overrides
  };
}
